import { useEffect, useId, useRef, type ReactNode } from 'react';

/**
 * The sheet the directory's filters live in. It is a bottom sheet rather than a centred dialog
 * because it is opened one-handed on a phone, by a thumb that is already at the bottom of the
 * screen — and it is a native `<dialog>` for the same reason the console's is: the focus trap, the
 * escape key and the inert background behind it come from the browser rather than from three
 * hooks that each have to be right.
 *
 * On a laptop it centres instead. The gesture it was shaped for does not exist there, and a panel
 * glued to the bottom edge of a 900px window is the shape that makes a desktop screen look like a
 * phone screenshot.
 *
 * Nothing here is applied or cancelled. The list behind the sheet filters as each choice is made,
 * so the footer button closes the sheet and says how many people are left rather than promising a
 * change the attendee has already watched happen.
 */
export function FilterSheet({
  open,
  title,
  onClose,
  footer,
  children,
}: {
  open: boolean;
  title: string;
  onClose: () => void;
  footer?: ReactNode;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();

  useEffect(() => {
    const element = ref.current;
    if (!element) {
      return;
    }

    if (open && !element.open) {
      element.showModal();
    } else if (!open && element.open) {
      element.close();
    }
  }, [open]);

  return (
    <dialog
      ref={ref}
      // Escape, and both buttons, all end up here: `close` fires however the sheet was dismissed,
      // so it is reported exactly once.
      onClose={onClose}
      // Tapping the dimmed area is how a sheet is dismissed on a phone, and the browser does not
      // do it — a modal dialog ignores the backdrop unless it is asked not to. A click on the
      // backdrop is a click on the dialog itself, and the column inside covers the sheet
      // completely, so this cannot fire for a tap on anything in it.
      onClick={(sheetClick) => {
        if (sheetClick.target === sheetClick.currentTarget) {
          onClose();
        }
      }}
      aria-labelledby={titleId}
      // Pinned to the bottom of the viewport and to the column's width: `mt-auto` is what moves a
      // modal dialog down, since its margins are resolved against the viewport rather than the page.
      //
      // Nothing here may set `display`, and the column below is a child for that reason alone. A
      // closed dialog is hidden by one line of the browser's own stylesheet — `display: none` — so a
      // `flex` on this element is an override that leaves the sheet on the screen permanently, open
      // or not, with a Close button that reports the close and changes nothing visible.
      className="rounded-t-card mx-auto mt-auto mb-0 w-full max-w-screen-sm overflow-hidden border border-border bg-surface p-0 text-ink backdrop:bg-ink/40 lg:m-auto lg:max-w-lg lg:rounded-card"
    >
      <div className="flex max-h-[85dvh] flex-col">
        <div className="px-gutter flex items-center gap-3 border-b border-border-2 pt-4 pb-3">
          <h2 id={titleId} className="font-display text-card-title flex-1 font-semibold text-ink">
            {title}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="text-subtitle min-h-tap rounded-button px-3 font-semibold text-ink-2"
          >
            Close
          </button>
        </div>

        <div className="px-gutter min-h-0 flex-1 overflow-y-auto py-4">{children}</div>

        {footer && (
          <div className="px-gutter border-t border-border-2 bg-surface pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
            {footer}
          </div>
        )}
      </div>
    </dialog>
  );
}
